<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import PageLayout from 'dashboard/components-next/captain/PageLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import TabBar from 'dashboard/components-next/tabbar/TabBar.vue';
import AssistantTrainingScriptsForm from 'dashboard/components-next/captain/pageComponents/assistant/settings/AssistantTrainingScriptsForm.vue';
import AssistantPlayground from 'dashboard/components-next/captain/assistant/AssistantPlayground.vue';
import CreateResponseDialog from 'dashboard/components-next/captain/pageComponents/response/CreateResponseDialog.vue';
import CreateDocumentDialog from 'dashboard/components-next/captain/pageComponents/document/CreateDocumentDialog.vue';
import ConnectInboxDialog from 'dashboard/components-next/captain/pageComponents/inbox/ConnectInboxDialog.vue';

const STEPS = ['scripts', 'answers', 'practice', 'live'];

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const store = useStore();

const scriptsForm = ref(null);
const createResponseDialog = ref(null);
const createDocumentDialog = ref(null);
const connectInboxDialog = ref(null);
const dialogType = ref('');

const assistantId = computed(() => Number(route.params.assistantId));
const assistant = computed(() =>
  store.getters['captainAssistants/getRecord'](assistantId.value)
);
const uiFlags = useMapGetter('captainAssistants/getUIFlags');
const isFetching = computed(() => uiFlags.value.fetchingItem);
const responses = useMapGetter('captainResponses/getRecords');
const documents = useMapGetter('captainDocuments/getRecords');
const captainInboxes = useMapGetter('captainInboxes/getRecords');
const approvedFaqs = computed(() =>
  responses.value.filter(item => item.status === 'approved')
);
const approvedFaqCount = computed(() => approvedFaqs.value.length);
const recentApprovedFaqs = computed(() =>
  approvedFaqs.value.slice(-5).reverse()
);

const currentStep = computed(() => {
  const step = route.query.step;
  return STEPS.includes(step) ? step : 'scripts';
});

const currentIndex = computed(() => STEPS.indexOf(currentStep.value));
const isFirst = computed(() => currentIndex.value <= 0);
const isLast = computed(() => currentIndex.value === STEPS.length - 1);

const steps = computed(() =>
  STEPS.map((key, index) => ({
    key,
    index,
    label: t(`CAPTAIN.TRAIN.STEPS.${key.toUpperCase()}.LABEL`),
    title: t(`CAPTAIN.TRAIN.STEPS.${key.toUpperCase()}.TITLE`),
    description: t(`CAPTAIN.TRAIN.STEPS.${key.toUpperCase()}.DESCRIPTION`),
  }))
);

const tabs = computed(() =>
  steps.value.map(step => ({
    label: step.label,
    value: step.key,
  }))
);

const goToStep = step => {
  router.replace({
    name: 'captain_assistants_train_index',
    params: route.params,
    query: { step },
  });
};

const handleTabChange = tab => {
  if (tab?.value) goToStep(tab.value);
};

const handleSubmit = async updatedAssistant => {
  try {
    await store.dispatch('captainAssistants/update', {
      id: assistantId.value,
      ...updatedAssistant,
    });
    useAlert(t('CAPTAIN.ASSISTANTS.EDIT.SUCCESS_MESSAGE'));
    if (!isLast.value) goToStep(STEPS[currentIndex.value + 1]);
  } catch (error) {
    useAlert(error?.message || t('CAPTAIN.ASSISTANTS.EDIT.ERROR_MESSAGE'));
  }
};

const handleNext = () => {
  if (currentStep.value === 'scripts') {
    scriptsForm.value?.submit();
    return;
  }
  if (!isLast.value) goToStep(STEPS[currentIndex.value + 1]);
};

const handleBack = () => {
  if (!isFirst.value) goToStep(STEPS[currentIndex.value - 1]);
};

const openFaqDialog = () => {
  dialogType.value = 'faq';
  nextTick(() => createResponseDialog.value.dialogRef.open());
};

const openDocumentDialog = () => {
  dialogType.value = 'document';
  nextTick(() => createDocumentDialog.value.dialogRef.open());
};

const openInboxDialog = () => {
  dialogType.value = 'inbox';
  nextTick(() => connectInboxDialog.value.dialogRef.open());
};

watch(
  () => route.query.step,
  step => {
    if (step === 'shop' || step === 'rules') {
      router.replace({
        name: 'captain_assistants_brain_index',
        params: route.params,
      });
    }
  },
  { immediate: true }
);

watch(
  assistantId,
  newId => {
    if (!newId) return;
    store.dispatch('captainAssistants/show', newId);
    store.dispatch('captainResponses/get', { assistantId: newId });
    store.dispatch('captainDocuments/get', { assistantId: newId });
    store.dispatch('captainInboxes/get', { assistantId: newId });
    store.dispatch('inboxes/get');
  },
  { immediate: true }
);
</script>

<template>
  <PageLayout
    :header-title="t('CAPTAIN.TRAIN.HEADER')"
    :is-fetching="isFetching"
    :show-pagination-footer="false"
    :show-know-more="false"
  >
    <template #body>
      <div class="flex flex-col gap-6 pb-8">
        <p class="mb-0 text-sm text-n-slate-11">
          {{ t('CAPTAIN.TRAIN.INTRO') }}
        </p>
        <TabBar
          :tabs="tabs"
          :initial-active-tab="currentIndex"
          class="w-full max-w-2xl [&>button]:flex-1 bg-n-alpha-black2"
          @tab-changed="handleTabChange"
        />

        <div class="flex flex-col gap-2">
          <h3 class="mb-0 text-lg font-medium text-n-slate-12">
            {{ steps[currentIndex].title }}
          </h3>
          <p class="mb-0 text-sm text-n-slate-11">
            {{ steps[currentIndex].description }}
          </p>
        </div>

        <AssistantTrainingScriptsForm
          v-if="currentStep === 'scripts'"
          ref="scriptsForm"
          :key="assistant?.config?.training?.applied_pack || 'none'"
          :assistant="assistant"
          :show-submit="false"
          @submit="handleSubmit"
        />

        <div v-else-if="currentStep === 'answers'" class="flex flex-col gap-4">
          <p class="mb-0 text-sm text-n-slate-12">
            {{
              t('CAPTAIN.TRAIN.ANSWERS.COUNTS', {
                faqs: approvedFaqCount,
                documents: documents.length,
              })
            }}
          </p>
          <div class="flex flex-wrap gap-2">
            <Button
              :label="t('CAPTAIN.TRAIN.ANSWERS.ADD_FAQ')"
              @click="openFaqDialog"
            />
            <Button
              faded
              :label="t('CAPTAIN.TRAIN.ANSWERS.ADD_DOCUMENT')"
              @click="openDocumentDialog"
            />
            <Button
              ghost
              :label="t('CAPTAIN.TRAIN.ANSWERS.VIEW_FAQS')"
              @click="
                router.push({
                  name: 'captain_assistants_responses_index',
                  params: route.params,
                })
              "
            />
            <Button
              ghost
              :label="t('CAPTAIN.TRAIN.ANSWERS.VIEW_DOCUMENTS')"
              @click="
                router.push({
                  name: 'captain_assistants_documents_index',
                  params: route.params,
                })
              "
            />
          </div>
          <div v-if="recentApprovedFaqs.length" class="flex flex-col gap-1">
            <p class="mb-0 text-sm text-n-slate-11">
              {{ t('CAPTAIN.TRAIN.ANSWERS.RECENT_TITLE') }}
            </p>
            <ul class="mb-0 list-disc pl-5 text-sm text-n-slate-12">
              <li
                v-for="faq in recentApprovedFaqs"
                :key="faq.id"
                class="truncate"
              >
                {{ faq.question }}
              </li>
            </ul>
          </div>
        </div>

        <div v-else-if="currentStep === 'practice'" class="flex flex-col gap-3">
          <p class="mb-0 text-sm text-n-slate-11">
            {{ t('CAPTAIN.TRAIN.PRACTICE.INTRO') }}
          </p>
          <div class="flex flex-col h-[32rem]">
            <AssistantPlayground
              :assistant-id="assistantId"
              class="bg-n-solid-1"
            />
          </div>
        </div>

        <div v-else-if="currentStep === 'live'" class="flex flex-col gap-4">
          <p class="mb-0 text-sm text-n-slate-12">
            {{
              t('CAPTAIN.TRAIN.LIVE.COUNTS', {
                count: captainInboxes.length,
              })
            }}
          </p>
          <p class="mb-0 text-sm text-n-amber-11">
            {{ t('CAPTAIN.TRAIN.LIVE.WARNING') }}
          </p>
          <p v-if="!captainInboxes.length" class="mb-0 text-sm text-n-slate-11">
            {{ t('CAPTAIN.TRAIN.LIVE.EMPTY') }}
          </p>
          <ul v-else class="mb-0 pl-5 text-sm text-n-slate-12">
            <li v-for="inbox in captainInboxes" :key="inbox.id">
              {{ inbox.name }}
            </li>
          </ul>
          <Button
            :label="t('CAPTAIN.TRAIN.LIVE.CONNECT')"
            @click="openInboxDialog"
          />
        </div>

        <div class="flex justify-between pt-2">
          <Button
            ghost
            :disabled="isFirst"
            :label="t('CAPTAIN.TRAIN.BACK')"
            @click="handleBack"
          />
          <Button
            v-if="!isLast"
            :label="t('CAPTAIN.TRAIN.NEXT')"
            @click="handleNext"
          />
          <Button
            v-else
            :label="t('CAPTAIN.TRAIN.DONE')"
            @click="
              router.push({
                name: 'captain_assistants_behavior_index',
                params: route.params,
              })
            "
          />
        </div>
      </div>

      <CreateResponseDialog
        v-if="dialogType === 'faq'"
        ref="createResponseDialog"
        type="create"
        @close="dialogType = ''"
      />
      <CreateDocumentDialog
        v-if="dialogType === 'document'"
        ref="createDocumentDialog"
        :assistant-id="assistantId"
        @close="dialogType = ''"
      />
      <ConnectInboxDialog
        v-if="dialogType === 'inbox'"
        ref="connectInboxDialog"
        :assistant-id="assistantId"
        @close="dialogType = ''"
      />
    </template>
  </PageLayout>
</template>
